import * as Yup from 'yup';
import { dayTypes } from '../../../../../helper';
import {
  AUTO_BLOCK_MAX_MINUTES,
  buildAmenitySchedulesFromDailyForm,
  getLongestContinuousAvailabilityMins,
  isValidAutoBlockDuration,
  LONGEST_AVAILABILITY_UNBOUNDED_MINS,
  MAX_ADVANCE_BOOKING_DAYS,
  maxAllowedAutoBlockMinutes,
  normalizeSlotDurationMinutes,
  parseScheduleHour,
  selectedDaysToCodes,
  WEEK_DAY_CODES
} from './addAmenityScheduleHelpersV2';
import {
  AMENITY_COST_TYPE,
  hasAtMostDecimalPlaces,
  isValidCostTypeForBooking,
  parsePaymentAmount
} from './amenityPaymentConfigV2';

const AUTO_BLOCK_NO_ROOM_DEFAULT =
  'The hours of operation of your amenity must be long enough to contain at least one booking plus a minimum post-booking block duration. Please increase the hours of operation, or decrease the minimum booking duration';

export const dayScheduleRowSchema = Yup.object({
  start: Yup.mixed().nullable(),
  end: Yup.mixed().nullable()
});

export const createEmptyDailySchedule = () =>
  WEEK_DAY_CODES.reduce((acc, code) => {
    acc[code] = { start: null, end: null };
    return acc;
  }, {});

/** Default slot length; post-booking block duration starts on this grid. */
export const DEFAULT_SLOT_DURATION_MINUTES = 60;

export const amenityDefaultValues = {
  type_id: '',
  code: '',
  rich_description: '',
  selectedDays: {},
  daily_schedule: createEmptyDailySchedule(),
  available_start: null,
  available_end: null,
  max_reservation_hours: null,
  min_reservation_hours: 0,
  allow_same_day_multiple_bookings: true,
  slot_duration_minutes: DEFAULT_SLOT_DURATION_MINUTES,
  allow_booking: true,
  allow_multi_day: false,
  booking_limits_unit: 0,
  min_duration_value: null,
  max_duration_value: null,
  max_advance_booking: 30,
  require_reservation_form: false,
  require_payment: false,
  cost_type: AMENITY_COST_TYPE.NONE,
  payment_rate: null,
  service_charge: null,
  deposit: null,
  require_manager_approval: false,
  enable_post_booking_auto_block: false,
  post_booking_auto_block_duration: DEFAULT_SLOT_DURATION_MINUTES,
  post_block_description: '',
  data: {},
  building_ids: [],
  waiver_upload: null
};

export const getAmenityValidationSchema = (t) =>
  Yup.object().shape({
    selectedDays: Yup.mixed().test('is-required', t('Required!'), (value) => {
      if (value?.value === dayTypes.SELECT_DAYS) return false;
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          if (value.length === 1 && value[0].value === dayTypes.SELECT_DAYS) return false;
          if (value.length > 0) return true;
          return false;
        }
        if (value.value) return true;
      }
      return false;
    }),
    daily_schedule: Yup.object(
      WEEK_DAY_CODES.reduce((acc, code) => {
        acc[code] = dayScheduleRowSchema;
        return acc;
      }, {})
    ).test('selected-days-schedule', function (value) {
      const selectedCodes = selectedDaysToCodes(this.parent.selectedDays);
      if (!selectedCodes.length) return true;
      const requiredMsg = t('Required!');
      const endAfterStartMsg = t('add_amenity_schedule_end_after_start', {
        defaultValue: 'End time must be after start time.'
      });
      for (const code of selectedCodes) {
        const row = value?.[code];
        const start = parseScheduleHour(row?.start);
        const end = parseScheduleHour(row?.end);
        if (start == null) {
          return this.createError({
            path: `${this.path}.${code}.start`,
            message: requiredMsg
          });
        }
        if (end == null) {
          return this.createError({
            path: `${this.path}.${code}.end`,
            message: requiredMsg
          });
        }
        if (end <= start) {
          return this.createError({
            path: `${this.path}.${code}.end`,
            message: endAfterStartMsg
          });
        }
      }
      return true;
    }),
    type_id: Yup.string()
      .typeError(t('Required!'))
      .test('is-required', t('Required!'), (value) => {
        if (value < 0 || !value) return false;
        return true;
      }),
    code: Yup.string().required(t('Required!')),
    rich_description: Yup.string().nullable(),
    allow_booking: Yup.boolean(),
    require_reservation_form: Yup.boolean(),
    require_manager_approval: Yup.boolean(),
    enable_post_booking_auto_block: Yup.boolean(),
    post_booking_auto_block_duration: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when(
        [
          'enable_post_booking_auto_block',
          'slot_duration_minutes',
          'allow_multi_day',
          'booking_limits_unit',
          'min_duration_value',
          'min_reservation_hours',
          'selectedDays',
          'daily_schedule'
        ],
        {
          is: (enabled) => enabled === true,
          then: (schema) =>
            schema
              .typeError(t('Required!'))
              .required(t('Required!'))
              .test('auto-block-duration', function (value) {
                const p = this.parent;
                const slot = normalizeSlotDurationMinutes(p.slot_duration_minutes);
                const maxAllowed = maxAllowedAutoBlockMinutes({
                  bookingLimitsUnit: p.booking_limits_unit,
                  allowMultiDay: !!p.allow_multi_day,
                  slotMinutes: slot,
                  selectedDays: p.selectedDays,
                  dailySchedule: p.daily_schedule,
                  minDurationValue: p.min_duration_value,
                  minReservationHours: p.min_reservation_hours
                });
                if (isValidAutoBlockDuration(value, slot, maxAllowed)) {
                  return true;
                }
                if (maxAllowed < slot) {
                  return this.createError({
                    message: t('add_amenity_auto_block_no_room', {
                      defaultValue: AUTO_BLOCK_NO_ROOM_DEFAULT
                    })
                  });
                }
                if (Number(value) > maxAllowed) {
                  return this.createError({
                    message: t('add_amenity_auto_block_too_long_for_day', {
                      defaultValue:
                        'Block time is too long. At least one open period must fit a booking plus this block time.'
                    })
                  });
                }
                return this.createError({
                  message: t('add_amenity_auto_block_duration_invalid', {
                    defaultValue:
                      'Block time must be a multiple of the slot duration, up to 6 hours.',
                    maxMinutes: AUTO_BLOCK_MAX_MINUTES
                  })
                });
              }),
          otherwise: (schema) => schema.notRequired().nullable()
        }
      ),
    post_block_description: Yup.string().nullable(),
    allow_same_day_multiple_bookings: Yup.boolean(),
    slot_duration_minutes: Yup.number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .typeError(t('Required!'))
      .oneOf([30, 60], t('Required!'))
      .required(t('Required!')),
    allow_multi_day: Yup.boolean(),
    booking_limits_unit: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when(['allow_booking', 'allow_multi_day'], {
        is: (allowBooking, allowMultiDay) => allowBooking === true && allowMultiDay === true,
        then: (schema) => schema.oneOf([0, 1]).typeError(t('Required!')).required(t('Required!')),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    max_advance_booking: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when('allow_booking', {
        is: true,
        then: (schema) =>
          schema
            .typeError(t('Required!'))
            .integer()
            .min(1)
            .max(
              MAX_ADVANCE_BOOKING_DAYS,
              t('add_amenity_max_advance_cap', {
                defaultValue: `Maximum advance booking limit is allowed up to ${MAX_ADVANCE_BOOKING_DAYS} days.`
              })
            )
            .required(t('Required!')),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    min_duration_value: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when(['allow_booking', 'allow_multi_day'], {
        is: (allowBooking, allowMultiDay) => allowBooking === true && allowMultiDay === true,
        then: (schema) =>
          schema.test('min-duration-multi-day', function (value) {
            const p = this.parent;
            const unit = Number(p.booking_limits_unit);
            const slot = Number(p.slot_duration_minutes);

            const effectiveMin =
              value === undefined || value === null || value === '' ? 0 : Number(value);

            if (Number.isNaN(effectiveMin)) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_minimum_invalid_days', {
                        defaultValue: 'Enter a valid minimum number of days.'
                      })
                    : t('add_amenity_minimum_invalid_hours', {
                        defaultValue: 'Enter a valid minimum number of hours.'
                      })
              });
            }
            if (effectiveMin < 0) {
              return this.createError({
                message: t('add_amenity_min_non_negative', {
                  defaultValue: 'Minimum cannot be negative.'
                })
              });
            }
            if (unit === 1 && effectiveMin < 1) {
              return this.createError({
                message: t('add_amenity_minimum_days_at_least_one', {
                  defaultValue: 'Minimum days must be at least 1.'
                })
              });
            }

            if (
              value !== undefined &&
              value !== null &&
              value !== '' &&
              !Number.isInteger(Number(value))
            ) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_booking_whole_days_only', {
                        defaultValue:
                          'Booking duration must be a whole number of days (no decimals).'
                      })
                    : t('add_amenity_booking_whole_numbers_only', {
                        defaultValue: 'Booking duration must be a whole number (no decimals).'
                      })
              });
            }

            if (effectiveMin > 0 && unit === 0) {
              const mins = effectiveMin * 60;
              if (!slot || slot <= 0 || mins % slot !== 0) {
                return this.createError({
                  message: t('add_amenity_duration_slot_align', {
                    defaultValue: 'Duration must align with slot length.'
                  })
                });
              }
            }

            const maxDur = p.max_duration_value;
            if (
              maxDur != null &&
              maxDur !== '' &&
              !Number.isNaN(Number(maxDur)) &&
              effectiveMin > Number(maxDur)
            ) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_min_days_must_lte_max_days', {
                        defaultValue: 'Minimum days must be less than or equal to maximum days.'
                      })
                    : t('add_amenity_min_hours_must_lte_max_hours', {
                        defaultValue:
                          'Minimum hours must be less than or equal to maximum hours.'
                      })
              });
            }

            // Min is int hours/days: must actually fit the open window (unlike max, which may ceil).
            if (effectiveMin > 0) {
              const schedules = buildAmenitySchedulesFromDailyForm(
                p.selectedDays,
                p.daily_schedule
              );
              const longestMins = getLongestContinuousAvailabilityMins(
                schedules,
                p.allow_multi_day === true
              );
              if (
                longestMins > 0 &&
                longestMins < LONGEST_AVAILABILITY_UNBOUNDED_MINS
              ) {
                const minMins =
                  unit === 1 ? effectiveMin * 24 * 60 : effectiveMin * 60;
                if (minMins > longestMins) {
                  const limit =
                    unit === 1
                      ? Math.floor(longestMins / (24 * 60))
                      : Math.floor(longestMins / 60);
                  return this.createError({
                    message: t('add_amenity_min_exceeds_schedule_limit', {
                      defaultValue:
                        'Minimum cannot exceed the schedule\'s longest open period ({{limit}} {{unitLabel}}).',
                      limit: String(limit),
                      unitLabel:
                        unit === 1
                          ? t('add_amenity_unit_days_lower', { defaultValue: 'days' })
                          : t('add_amenity_unit_hours_lower', { defaultValue: 'hours' })
                    })
                  });
                }
              }
            }

            return true;
          }),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    max_duration_value: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when(['allow_booking', 'allow_multi_day'], {
        is: (allowBooking, allowMultiDay) => allowBooking === true && allowMultiDay === true,
        then: (schema) =>
          schema.test('max-duration-multi-day', function (value) {
            const p = this.parent;
            const unit = Number(p.booking_limits_unit);
            const slot = Number(p.slot_duration_minutes);
            const schedules = buildAmenitySchedulesFromDailyForm(
              p.selectedDays,
              p.daily_schedule
            );
            const longestMins = getLongestContinuousAvailabilityMins(
              schedules,
              p.allow_multi_day === true
            );

            const advanceRaw = p.max_advance_booking;
            const advanceDays = Math.min(
              MAX_ADVANCE_BOOKING_DAYS,
              advanceRaw != null &&
                advanceRaw !== '' &&
                !Number.isNaN(Number(advanceRaw)) &&
                Number(advanceRaw) > 0
                ? Number(advanceRaw)
                : 30
            );

            const maxNum = Number(value);
            if (value === undefined || value === null || Number.isNaN(maxNum)) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_maximum_days_required', {
                        defaultValue: 'Maximum days required.'
                      })
                    : t('add_amenity_maximum_hours_required', {
                        defaultValue: 'Maximum hours required.'
                      })
              });
            }
            if (maxNum === 0) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_maximum_days_cannot_be_zero', {
                        defaultValue: 'Maximum days cannot be 0.'
                      })
                    : t('add_amenity_maximum_hours_cannot_be_zero', {
                        defaultValue: 'Maximum hours cannot be 0.'
                      })
              });
            }
            if (maxNum < 0) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_maximum_days_must_be_positive', {
                        defaultValue: 'Maximum days must be greater than 0.'
                      })
                    : t('add_amenity_maximum_hours_must_be_positive', {
                        defaultValue: 'Maximum hours must be greater than 0.'
                      })
              });
            }

            const minVEffective =
              p.min_duration_value === undefined ||
              p.min_duration_value === null ||
              p.min_duration_value === ''
                ? 0
                : Number(p.min_duration_value);
            if (!Number.isNaN(minVEffective) && maxNum < minVEffective) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_max_days_must_gte_min_days', {
                        defaultValue:
                          'Maximum days must be greater than or equal to minimum days.'
                      })
                    : t('add_amenity_max_hours_must_gte_min_hours', {
                        defaultValue:
                          'Maximum hours must be greater than or equal to minimum hours.'
                      })
              });
            }

            if (!Number.isInteger(maxNum)) {
              return this.createError({
                message:
                  unit === 1
                    ? t('add_amenity_booking_whole_days_only', {
                        defaultValue:
                          'Booking duration must be a whole number of days (no decimals).'
                      })
                    : t('add_amenity_booking_whole_numbers_only', {
                        defaultValue: 'Booking duration must be a whole number (no decimals).'
                      })
              });
            }

            if (unit === 1) {
              const num = maxNum;
              if (longestMins === LONGEST_AVAILABILITY_UNBOUNDED_MINS) {
                if (num > advanceDays) {
                  return this.createError({
                    message: t('add_amenity_err_max_duration_advance_days', {
                      defaultValue: 'Cannot exceed max advance booking (days).'
                    })
                  });
                }
              } else {
                const maxAllowedDays = Math.ceil(longestMins / (24 * 60));
                if (num > maxAllowedDays) {
                  return this.createError({
                    message: t('add_amenity_max_exceeds_schedule_limit', {
                      defaultValue:
                        'Schedule of this amenity allows {{limit}} {{unitLabel}} for maximum possible booking.',
                      limit: String(maxAllowedDays),
                      unitLabel: t('add_amenity_unit_days_lower', { defaultValue: 'days' })
                    })
                  });
                }
              }
              return true;
            }

            if (unit === 0) {
              const mins = maxNum * 60;
              if (!slot || slot <= 0 || mins % slot !== 0) {
                return this.createError({
                  message: t('add_amenity_duration_slot_align', {
                    defaultValue: 'Duration must align with slot length.'
                  })
                });
              }
              const valueMins = mins;
              if (longestMins === LONGEST_AVAILABILITY_UNBOUNDED_MINS) {
                const capMins = advanceDays * 24 * 60;
                if (valueMins > capMins) {
                  return this.createError({
                    message: t('add_amenity_err_max_duration_advance_hours', {
                      defaultValue: 'Cannot exceed max advance booking window.'
                    })
                  });
                }
              } else {
                const maxAllowedHours = Math.ceil(longestMins / 60);
                if (maxNum > maxAllowedHours) {
                  return this.createError({
                    message: t('add_amenity_max_exceeds_schedule_limit', {
                      defaultValue:
                        'Schedule of this amenity allows {{limit}} {{unitLabel}} for maximum possible booking.',
                      limit: String(maxAllowedHours),
                      unitLabel: t('add_amenity_unit_hours_lower', { defaultValue: 'hours' })
                    })
                  });
                }
              }
              return true;
            }

            return true;
          }),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    min_reservation_hours: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined ? 0 : value
      )
      .when(['allow_booking', 'allow_multi_day'], {
        is: (allowBooking, allowMultiDay) => allowBooking === true && allowMultiDay !== true,
        then: (schema) =>
          schema
            .typeError(
              t('add_amenity_minimum_invalid', {
                defaultValue: 'Enter a valid minimum.'
              })
            )
            .integer(
              t('add_amenity_booking_whole_numbers_only', {
                defaultValue: 'Booking duration must be a whole number (no decimals).'
              })
            )
            .min(
              0,
              t('add_amenity_min_non_negative', {
                defaultValue: 'Minimum cannot be negative.'
              })
            )
            .test(
              'min-reservation-lte-max',
              t('add_amenity_min_must_lte_max', {
                defaultValue: 'Minimum must be less than or equal to maximum.'
              }),
              function (value) {
                const maxH = this.parent.max_reservation_hours;
                if (maxH == null || maxH === '' || Number.isNaN(Number(maxH))) return true;
                const minN =
                  value === null || value === '' || Number.isNaN(Number(value)) ? 0 : Number(value);
                return minN <= Number(maxH);
              }
            )
            .test('min-reservation-fits-schedule', function (value) {
              const minN =
                value === null || value === '' || Number.isNaN(Number(value)) ? 0 : Number(value);
              if (minN <= 0) return true;

              const schedules = buildAmenitySchedulesFromDailyForm(
                this.parent.selectedDays,
                this.parent.daily_schedule
              );
              const longestMins = getLongestContinuousAvailabilityMins(schedules, false);
              if (longestMins <= 0 || longestMins >= LONGEST_AVAILABILITY_UNBOUNDED_MINS) {
                return true;
              }

              if (minN * 60 <= longestMins) return true;

              return this.createError({
                message: t('add_amenity_min_exceeds_schedule_limit', {
                  defaultValue:
                    'Minimum cannot exceed the schedule\'s longest open period ({{limit}} {{unitLabel}}).',
                  limit: String(Math.floor(longestMins / 60)),
                  unitLabel: t('add_amenity_unit_hours_lower', { defaultValue: 'hours' })
                })
              });
            }),
        otherwise: (schema) => schema.notRequired()
      }),
    max_reservation_hours: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .when(['allow_booking', 'allow_multi_day'], {
        is: (allowBooking, allowMultiDay) => allowBooking === true && allowMultiDay !== true,
        then: (schema) =>
          schema
            .typeError(
              t('add_amenity_maximum_invalid', {
                defaultValue: 'Enter a valid maximum.'
              })
            )
            .integer(
              t('add_amenity_booking_whole_numbers_only', {
                defaultValue: 'Booking duration must be a whole number (no decimals).'
              })
            )
            .max(99999, t('Not more than 5 characters!'))
            .min(1, t("Max time can't be 0!"))
            .required(
              t('add_amenity_maximum_required', {
                defaultValue: 'Maximum required.'
              })
            )
            .test('max-reservation-hours', function (value) {
              if (!(value > 0)) {
                return true;
              }
              const selectedCodes = selectedDaysToCodes(this.parent.selectedDays);
              if (!selectedCodes.length) {
                return true;
              }

              let maxDailyWindow = 0;
              for (const code of selectedCodes) {
                const row = this.parent.daily_schedule?.[code];
                if (!row || row.start == null || row.end == null) {
                  continue;
                }
                const startH = parseScheduleHour(row.start);
                const endH = parseScheduleHour(row.end);
                if (startH == null || endH == null) {
                  continue;
                }
                const dailyWindow = endH - startH;
                if (Number.isNaN(dailyWindow) || dailyWindow <= 0) {
                  continue;
                }
                maxDailyWindow = Math.max(maxDailyWindow, dailyWindow);
              }

              if (!Number.isFinite(maxDailyWindow) || maxDailyWindow <= 0) {
                return true;
              }

              const maxAllowedHours = Math.ceil(maxDailyWindow);
              if (Number(value) <= maxAllowedHours) {
                return true;
              }

              return this.createError({
                message: t('add_amenity_max_exceeds_schedule_limit', {
                  defaultValue:
                    'Schedule of this amenity allows {{limit}} {{unitLabel}} for maximum possible booking.',
                  limit: String(maxAllowedHours),
                  unitLabel: t('add_amenity_unit_hours_lower', { defaultValue: 'hours' })
                })
              });
            })
            .test(
              'max-gte-min',
              t('Max hours must be greater than or equal to min hours'),
              (value, context) => {
                if (value == null || value === '') return true;
                const raw = context.parent.min_reservation_hours;
                const minH =
                  raw === null || raw === undefined || raw === '' ? 0 : Number(raw);
                if (Number.isNaN(minH)) return true;
                return Number(value) >= minH;
              }
            ),
        otherwise: (schema) => schema.notRequired()
      }),
    available_start: Yup.string().nullable(),
    available_end: Yup.string().nullable(),
    building_ids: Yup.array()
      .nullable()
      .notRequired()
      .transform((v) => (Array.isArray(v) ? v : [])),
    waiver_upload: Yup.mixed().when(['allow_booking', 'require_reservation_form'], {
      is: (allowBooking, requireForm) => allowBooking === true && requireForm === true,
      then: (schema) =>
        schema.test(
          'has-waiver',
          t('add_amenity_waiver_required_inline', {
            defaultValue: 'A PDF form is required when Require Amenity Form is enabled.'
          }),
          (value) => value != null
        ),
      otherwise: (schema) => schema.nullable().notRequired()
    }),
    cost_type: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when('require_payment', {
        is: true,
        then: (schema) =>
          schema
            .typeError(t('Required!'))
            .required(t('Required!'))
            .test('valid-cost-type', function (value) {
              const allowMultiDay = this.parent.allow_multi_day === true;
              if (!isValidCostTypeForBooking(value, allowMultiDay)) {
                return this.createError({
                  message: t('add_amenity_rate_type_required', {
                    defaultValue: 'Select rate type.'
                  })
                });
              }
              return true;
            }),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    payment_rate: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when('require_payment', {
        is: true,
        then: (schema) =>
          schema
            .typeError(
              t('add_amenity_payment_rate_invalid', {
                defaultValue: 'Enter a valid amount.'
              })
            )
            .required(t('Required!'))
            .test('payment-rate-positive', function (value) {
              const n = parsePaymentAmount(value);
              if (n == null || n <= 0) {
                return this.createError({
                  message: t('add_amenity_payment_rate_positive', {
                    defaultValue: 'Amount must be greater than 0.'
                  })
                });
              }
              if (!hasAtMostDecimalPlaces(value)) {
                return this.createError({
                  message: t('add_amenity_payment_rate_decimals', {
                    defaultValue: 'Amount can have at most 2 decimal places.'
                  })
                });
              }
              return true;
            }),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    service_charge: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when('require_payment', {
        is: true,
        then: (schema) =>
          schema
            .nullable()
            .notRequired()
            .test('service-charge-valid', function (value) {
              if (value === undefined || value === null || value === '') return true;
              const n = parsePaymentAmount(value);
              if (n == null || n < 0) {
                return this.createError({
                  message: t('add_amenity_service_charge_invalid', {
                    defaultValue: 'Enter a valid service fee amount.'
                  })
                });
              }
              if (!hasAtMostDecimalPlaces(value)) {
                return this.createError({
                  message: t('add_amenity_payment_rate_decimals', {
                    defaultValue: 'Amount can have at most 2 decimal places.'
                  })
                });
              }
              return true;
            }),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    deposit: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || originalValue === undefined
          ? undefined
          : Number(originalValue)
      )
      .when('require_payment', {
        is: true,
        then: (schema) =>
          schema
            .nullable()
            .notRequired()
            .test('deposit-valid', function (value) {
              if (value === undefined || value === null || value === '') return true;
              const n = parsePaymentAmount(value);
              if (n == null || n < 0) {
                return this.createError({
                  message: t('add_amenity_deposit_invalid', {
                    defaultValue: 'Enter a valid deposit amount.'
                  })
                });
              }
              if (!hasAtMostDecimalPlaces(value)) {
                return this.createError({
                  message: t('add_amenity_payment_rate_decimals', {
                    defaultValue: 'Amount can have at most 2 decimal places.'
                  })
                });
              }
              return true;
            }),
        otherwise: (schema) => schema.notRequired().nullable()
      })
  });

